import React, { useId, useState } from "react";
import { Box, Button, Menu, MenuItem, Typography } from "@mui/material";
import { RiArrowDownSLine, RiArrowLeftSLine, RiArrowRightSLine } from "@remixicon/react";
import { focusRing, tokens } from "../../utils/figmaTokens";

// Keyboard focus shows D22's ring, not MUI's grey focus ripple (047 D73).
const navSx = {
  p: "8px 10px",
  minWidth: 0,
  gap: "6px",
  borderRadius: "8px",
  fontSize: 14,
  lineHeight: "22px",
  fontWeight: 500,
  color: tokens.text.primary,
  "&.Mui-disabled": { color: tokens.text.disabled },
  ...focusRing,
};

// Admin · Pagination 10600:656 without page numbers (047 D16): the "Showing …" text, and
// Previous / Next when the list pages by cursor (`onPage`). A list whose page size staff can
// pick passes `pageSize`, `pageSizes` and `onPageSize`: "25 per page" sits beside the text
// (047 D33; Figma draws no size control).
const Pager = ({ label, page = 0, hasMore = false, onPage, pageSize, pageSizes = [], onPageSize }) => {
  const sizeId = useId();
  const [sizeAnchor, setSizeAnchor] = useState(null);
  const pickSize = (size) => {
    setSizeAnchor(null);
    onPageSize(size);
  };
  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px", px: "16px", py: "12px" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "12px", minWidth: 0 }}>
        <Typography variant="bodySmall" sx={{ color: tokens.text.tertiary }}>
          {label}
        </Typography>
        {onPageSize && (
          <>
            <Button
              id={`${sizeId}-button`}
              onClick={(e) => setSizeAnchor(e.currentTarget)}
              aria-haspopup="menu"
              aria-expanded={Boolean(sizeAnchor)}
              aria-controls={sizeAnchor ? `${sizeId}-menu` : undefined}
              disableFocusRipple
              sx={{ ...navSx, color: tokens.text.tertiary, fontWeight: 400 }}
            >
              {pageSize} per page
              <RiArrowDownSLine size={16} />
            </Button>
            <Menu
              id={`${sizeId}-menu`}
              anchorEl={sizeAnchor}
              open={Boolean(sizeAnchor)}
              onClose={() => setSizeAnchor(null)}
              slotProps={{ list: { "aria-labelledby": `${sizeId}-button` } }}
            >
              {pageSizes.map((size) => (
                <MenuItem key={size} selected={size === pageSize} onClick={() => pickSize(size)}>
                  {size} per page
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
      </Box>
      {onPage && (
        <Box sx={{ display: "flex", gap: "4px" }}>
          <Button onClick={() => onPage(page - 1)} disabled={page === 0} disableFocusRipple sx={navSx}>
            <RiArrowLeftSLine size={16} />
            Previous
          </Button>
          <Button onClick={() => onPage(page + 1)} disabled={!hasMore} disableFocusRipple sx={navSx}>
            Next
            <RiArrowRightSLine size={16} />
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default Pager;
